import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { createBrowserRouter, RouterProvider } from 'react-router';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster, toast } from 'react-hot-toast';
import { NuqsAdapter } from 'nuqs/adapters/react';
import './sentry';
import {
  AnalyticsPage,
  BillingHistoryPage,
  SubscriptionSummaryPage,
  ReferAndEarnPage,
  ConversationsPage,
  ConversationTemplatesPage,
  CustomersPage,
  DashboardOverviewPage,
  ForgotPasswordPage,
  KYBPage,
  LoginPage,
  ProductsPage,
  BulkProductUploadPage,
  ProfilePage,
  SalesPage,
  SettingsPage,
  ShippingPage,
  SignUpPage,
  StoreBuilderPage,
  StorePage,
  SupportPage,
  TransactionsPage,
} from './pages';
import { AuthLayout, DashboardLayout } from './components/layouts';
import { routes } from './lib/constants';
import { removeForwardSlashes } from './lib/helpers';
import './index.css';
import { GoogleOAuthProvider } from '@react-oauth/google';

const router = createBrowserRouter([
  {
    Component: AuthLayout,
    children: [
      {
        path: removeForwardSlashes(routes.forgotPassword),
        Component: ForgotPasswordPage,
      },
      { path: removeForwardSlashes(routes.login), Component: LoginPage },
      { path: removeForwardSlashes(routes.signUp), Component: SignUpPage },
    ],
  },
  {
    Component: DashboardLayout,
    children: [
      { index: true, Component: DashboardOverviewPage },
      {
        path: removeForwardSlashes(routes.conversations),
        children: [
          { index: true, Component: ConversationsPage },
          {
            path: 'templates',
            Component: ConversationTemplatesPage,
          },
        ],
      },
      {
        path: removeForwardSlashes(routes.sales),
        Component: SalesPage,
      },
      {
        path: removeForwardSlashes(routes.store),
        children: [
          { index: true, Component: StorePage },
          {
            path: removeForwardSlashes(routes.create),
            Component: StoreBuilderPage,
          },
        ],
      },
      {
        path: removeForwardSlashes(routes.products),
        children: [
          { index: true, Component: ProductsPage },
          {
            path: 'bulk-upload',
            Component: BulkProductUploadPage,
          },
        ],
      },
      {
        path: removeForwardSlashes(routes.customers),
        Component: CustomersPage,
      },
      {
        path: removeForwardSlashes(routes.transactions),
        Component: TransactionsPage,
      },
      {
        path: removeForwardSlashes(routes.shipping),
        Component: ShippingPage,
      },
      {
        path: removeForwardSlashes(routes.analytics),
        Component: AnalyticsPage,
      },
      {
        path: removeForwardSlashes(routes.settings),
        Component: SettingsPage,
      },
      {
        path: removeForwardSlashes(routes.support),
        Component: SupportPage,
      },
      {
        path: removeForwardSlashes(routes.profile),
        Component: ProfilePage,
      },
      {
        path: removeForwardSlashes(routes.kyb),
        Component: KYBPage,
      },
      {
        path: removeForwardSlashes(routes.billing),
        children: [
          { index: true, Component: BillingHistoryPage },
          { path: 'summary', Component: SubscriptionSummaryPage },
        ],
      },
      {
        path: removeForwardSlashes(routes.referAndEarn),
        Component: ReferAndEarnPage,
      },
    ],
  },
]);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Auth failures shouldn't "hang" the app via retries.
      retry: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    },
    mutations: {
      onError: (error) => {
        toast.error(error.message || 'An error occurred');
      },
      retry: false,
    },
  },
});

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <GoogleOAuthProvider clientId="760230014281-jc64g20545pi30tskb0oo4h9cvnbieq9.apps.googleusercontent.com">
      <NuqsAdapter>
        <QueryClientProvider client={queryClient}>
          <RouterProvider router={router} />
          <Toaster />
        </QueryClientProvider>
      </NuqsAdapter>
    </GoogleOAuthProvider>
  </StrictMode>
);
